import { MoreOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Menu, message, Modal } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import APSGeneralEducationApiService from '../../../../api/APSGeneralEducationApiService';
import AuthorizedContent from '../../../../components/AuthorizedContent';
import APSGeneralEducationDetailsForm from '../../../../components/forms/postSecondary/APSGeneralEducationDetailsForm';
import EditCancelButton from '../../../../components/buttons/EditCancelButton';
import Breadcrumbs from '../../../../config/Breadcrumbs';
import Routes from '../../../../config/Routes';
import FeatureFlag from '../../../../consts/FeatureFlag';
import * as DeleteAPSGeneralEducationHandler from '../../../../handlerModels/DeleteAPSGeneralEducationHandler';
import * as GetAPSGeneralEducationDetailsHandler from '../../../../handlerModels/GetAPSGeneralEducationDetailsHandler';
import APSGeneralEducationDTO from '../../../../models/APSGeneralEducationDTO';
import HeaderPortal from '../../../../portals/HeaderPortal';
import BaseFormState from '../../../../redux/bases/BaseFormState';
import HistoryUtil from '../../../../utils/HistoryUtil';
import PageStayPrompt from '../../../../utils/PageStayPrompt';
import ParameterUtil from '../../../../utils/ParameterUtil';

interface APSGeneralEducationDetailsPageState extends BaseFormState {
  apsGeneralEducationId: number;
  apsGeneralEducation?: APSGeneralEducationDTO;
}

interface APSGeneralEducationDetailsPageProps {
}

class APSGeneralEducationDetailsPage extends React.Component<APSGeneralEducationDetailsPageProps & RouteComponentProps<RouteObject>, APSGeneralEducationDetailsPageState> {
  private readonly _apsGeneralEducationDetailsRef = React.createRef<APSGeneralEducationDetailsForm>();

  constructor(props: APSGeneralEducationDetailsPageProps & RouteComponentProps<RouteObject>) {
    super(props);

    this.state = {
      apsGeneralEducationId: 0
    };
  }

  componentDidMount() {
    const apsGeneralEducationId = ParameterUtil.getPathPart(this.props.match, 'id');

    if (apsGeneralEducationId == 'new') {
      this.setState({ isEditing: true, isNew: true, loading: false })
    }
    else {
      const apsGeneralEducationIdNumber = Number.parseInt(apsGeneralEducationId);
      this.loadAPSGeneralEducation(apsGeneralEducationIdNumber);
    }
  }

  private loadAPSGeneralEducation = (apsGeneralEducationId: number) => {
    this.setState({ apsGeneralEducationId: apsGeneralEducationId });

    APSGeneralEducationApiService.getAPSGeneralEducationDetails(apsGeneralEducationId)
      .then((results: GetAPSGeneralEducationDetailsHandler.Result) => {
        if (results.apsGeneralEducation) {
          this.setState({ apsGeneralEducation: results.apsGeneralEducation });
        }
      }).catch(() => {
        this.setState({ error: true });
        message.error('APS General Education could not be loaded')
      }).finally(() => {
        this.setState({ loading: false });
      });
  }

  private deleteConfirmation = (id: number) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this APS General Education?',
      okText: 'Delete',
      onOk: () => this.deleteAPSGeneralEducation(id)
    });
  }

  private deleteAPSGeneralEducation = (apsGeneralEducationId: number) => {
    this.setState({ submitting: true });

    const request = DeleteAPSGeneralEducationHandler.Request.create({
      apsGeneralEducationId: apsGeneralEducationId,
    });

    APSGeneralEducationApiService.deleteAPSGeneralEducation(request)
      .then((result: DeleteAPSGeneralEducationHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
          message.success('APS General Education was removed.');
        }
        else {
          message.error(result?.errors.join('\n'));
        }

      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('APS General Education could not be removed.');
      })
      .finally(() => {
        this.setState({ loading: false });
        this.goToAPSGeneralEducation();
      });
  }

  private toggleEdit = () => {
    if (this.state.isEditing) {
      this._apsGeneralEducationDetailsRef.current?.resetForm();
    }

    this.setState({ isEditing: !this.state.isEditing });
  }

  private apsGeneralEducationDetailsSaved = (apsGeneralEducationId: number) => {
    this.setState({ isEditing: false });
    if (apsGeneralEducationId != this.state.apsGeneralEducationId) {
      HistoryUtil.replace(Routes.generate(Routes.APS_GENERAL_EDUCATION_DETAILS, { id: apsGeneralEducationId }));
    }
    else {
      this.loadAPSGeneralEducation(apsGeneralEducationId);
    }
  }

  private goToAPSGeneralEducation = () => {
    HistoryUtil.goBack();
  }

  render() {
    const title = this.state.loading ? '...' : this.state.apsGeneralEducationId == 0 ? 'New APS General Education' : this.state.apsGeneralEducation?.name ?? '...';

    return (
      <Content >
        <HeaderPortal
          title={title}
          onBack={this.goToAPSGeneralEducation}
          breadcrumbs={Breadcrumbs.apsGeneralEducationDetails(title, this.state.apsGeneralEducationId)}
          extra={this.renderMoreActions()} />
        <PageStayPrompt when={this.state.isEditing} />

        {this.renderDetailsCard()}
      </Content>
    );
  }

  renderDetailsCard() {
    const editButton = this.state.apsGeneralEducationId == 0 ?
      null :
      (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_APS_GENERAL_EDUCATION]}>
          <EditCancelButton isEditing={this.state.isEditing} onClick={this.toggleEdit} />
        </AuthorizedContent>
      );

    return (
      <Card title="Details" extra={editButton}>
        <APSGeneralEducationDetailsForm
          ref={this._apsGeneralEducationDetailsRef}
          apsGeneralEducationId={this.state.apsGeneralEducationId}
          isEditing={this.state.isEditing}
          isNew={this.state.isNew}
          onSave={this.apsGeneralEducationDetailsSaved} />
      </Card>
    );
  }

  renderMoreActions() {
    const menu = (
      <Menu>
        < Menu.Item title='Delete APS General Education' key={this.state.apsGeneralEducationId} onClick={() => this.deleteConfirmation(this.state.apsGeneralEducationId)}  >
          Delete APS General Education
        </Menu.Item>
      </Menu>
    );
    return (
      <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_APS_GENERAL_EDUCATION]}>
        <Dropdown overlay={menu}>
          <Button size='large' type='link' >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </AuthorizedContent>
    );
  }
}

export default withRouter(APSGeneralEducationDetailsPage);
